/**
 * Error boundary specifically for complex components like ComponentEditor and WorkflowEditor
 * Provides a localized error recovery mechanism without crashing the entire app
 */
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logger } from '../lib/utils/logger';
import type { AppError } from '../utils/error-utils';
import { categorizeError, getUserFriendlyMessage } from '../utils/error-utils';

interface Props {
  children: ReactNode;
  componentName?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: AppError;
  errorInfo?: ErrorInfo;
}

export class ComponentErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const appError = categorizeError(error);
    return {
      hasError: true,
      error: appError,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentName = this.props.componentName || 'Component';

    logger.error(`Error in ${componentName}:`, {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  private handleGoBack = () => {
    window.history.back();
  };

  public override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const { error } = this.state;
      const message = getUserFriendlyMessage(error);
      const componentName = this.props.componentName || 'component';

      return (
        <div className="p-4">
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-600 dark:bg-red-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-white shrink-0 mt-0.5" />
              <div className="flex-1">
                <h6 className="text-base font-semibold text-white mb-1">
                  Error in {componentName}
                </h6>
                <p className="text-sm text-white">{message}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 max-w-2xl mx-auto">
            <div className="p-4 text-center space-y-4">
              <p className="text-base text-zinc-600 dark:text-zinc-400">
                The {componentName} encountered an error and cannot be displayed. You can try
                refreshing the component or navigate back to the previous page.
              </p>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && error.details && (
                <div className="text-left px-4 py-3 rounded-lg bg-zinc-50 dark:bg-zinc-900/20 border border-zinc-200 dark:border-zinc-700">
                  <details>
                    <summary className="cursor-pointer text-sm font-medium text-zinc-900 dark:text-white">
                      Technical Details
                    </summary>
                    <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap select-text text-zinc-700 dark:text-zinc-300">
                      {error.details}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </details>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="flex items-center justify-center gap-2 px-4 py-2
                             bg-sky-600 dark:bg-sky-500 text-white rounded-lg
                             hover:bg-sky-700 dark:hover:bg-sky-600
                             focus:outline-hidden focus:ring-2 focus:ring-sky-500
                             transition-colors font-medium"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>

                <button
                  onClick={this.handleGoBack}
                  className="flex items-center justify-center gap-2 px-4 py-2
                             border-2 border-zinc-300 dark:border-zinc-600
                             text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 rounded-lg
                             hover:bg-zinc-50 dark:hover:bg-zinc-700
                             focus:outline-hidden focus:ring-2 focus:ring-zinc-300
                             transition-colors font-medium"
                >
                  <Home className="h-4 w-4" />
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
